import isEqual from 'lodash/isEqual';
import { batch } from 'react-redux';
import { push, replace } from 'connected-next-router';
import createAction from '@/Framework/State/Redux/createAction';
import isEmptyString from '@/Framework/dataHelpers/string/isEmptyString';
import { RESEARCH_STORE_NAME } from '@/finsight/application/actions/research/constants';
import { addQueryStringParam, removeQueryStringParam } from '@/Framework/url/helpers/queryString';
import ResearchRepository from '@/finsight/infrastructure/repository/ResearchRepository';
import researchFiltersSelectors from '@/finsight/application/actions/research/filters/selectors';
import * as actionTypes from './actionTypes';
import createAsyncRequest from '@/Framework/State/Redux/createAsyncRequest';
import { getAdditionalParams } from '@/finsight/application/helpers';

export const applyFilter = (filters) => (dispatch, getState) => {
  const state = getState();
  const { location } = getState().router;
  const initialFilters = researchFiltersSelectors.getInitialFilter(state);
  const filtersPayload = dispatch(getFiltersPayload(filters));

  const isInitialFilters = isEqual(initialFilters, filters);
  const appliedFilters = isInitialFilters ? {} : filters;
  dispatch(payloadChange(filtersPayload));
  dispatch(appliedFiltersChange(appliedFilters));

  if (isInitialFilters) {
    const newSearch = removeQueryStringParam(location, ['dateFrom', 'dateTo', 'hash']);
    dispatch(push(`${ location.pathname }?${ newSearch }`));

    return;
  }

  const additionalParams = getAdditionalParams(filtersPayload);

  if (Object.keys(additionalParams).length > 0) {
    const newSearch = addQueryStringParam(location, additionalParams);

    dispatch(replace(`${ location.pathname }?${ newSearch }`));
  }
};

export const fetchFilter = (filters) => async (dispatch, getState) => {
  const state = getState();
  const { location } = getState().router;
  const initialFilters = researchFiltersSelectors.getInitialFilter(state);
  const filtersPayload = dispatch(getFiltersPayload(filters));

  await dispatch(getResearchFilter(filtersPayload));
  const {
    filter: researchFilter = {},
    hash = '',
  } = researchFiltersSelectors.getFilterItems(getState());
  const isResearchFilter = !!Object.keys(researchFilter).length;
  const isInitialFilters = isEqual(initialFilters, filters);
  const isHash = isEmptyString(hash);
  if (isResearchFilter) {
    const { dateFrom, dateTo } = researchFilter;
    dispatch(setMinMaxDatePeriod(dateFrom, dateTo));
  }
  if (!isHash && !isInitialFilters) {
    const additionalParams = getAdditionalParams(filtersPayload);
    const newSearch = addQueryStringParam(location, { hash, ...additionalParams });

    dispatch(push(`${ location.pathname }?${ newSearch }`));
  }
};

export const getResearchFilter = (filtersPayload) => (dispatch, getState) => {
  const researchRepository = getState().container.get(ResearchRepository);

  return dispatch(createAsyncRequest(
    {
      payload: {
        filter: {
          ...filtersPayload,
        },
      },
      method: researchRepository.getResearchFilter,
      types: [
        actionTypes.GET_RESEARCH_FILTER, actionTypes.GET_RESEARCH_FILTER_SUCCESS,
        actionTypes.GET_RESEARCH_FILTER_ERROR,
      ],
    },
  ));
};

export const getResearchFilterByHash = (hash: string | string[]) => (_dispatch, getState) => {
  const researchRepository = getState().container.get(ResearchRepository);
  return researchRepository.getResearchFilterByHash(hash);
};

export const payloadChange = (payload) => createAction(actionTypes.RESEARCH_FILTER_PAYLOAD, payload);

export const initialFiltersChange = (payload) => createAction(actionTypes.RESEARCH_INITIAL_FILTERS, payload);

export const appliedFiltersChange = (payload) => createAction(actionTypes.RESEARCH_FILTER_APPLY, payload);

export const getResearchFiltersInitialState = () => ({
  dateFrom: null,
  dateTo: null,
  includedDocumentTypeIds: [],
  includedPseudoSectorIds: [],
  includedPublisherIds: [],
});

export const getFiltersPayload = (filters = {}) => () => {
  return filters;
};

export const setMinMaxDatePeriod = (dateFrom: number, dateTo: number) => (dispatch) => {
  batch(() => {
    dispatch(createAction(actionTypes.RESEARCH_FILTER_MIN_DATE_FROM, dateFrom));
    dispatch(createAction(actionTypes.RESEARCH_FILTER_MAX_DATE_TO, dateTo));
  });
};

export const initFilters = (
  filtersPayload,
  filtersState = getResearchFiltersInitialState(),
  isHashPayload: boolean = false,
  filtersUpdate,
) => async (dispatch, getState) => {
  await dispatch(getResearchFilter(filtersPayload));
  const { filter: researchFilter } = researchFiltersSelectors.getFilterItems(getState());
  dispatch(initialFiltersChange(getResearchFiltersInitialState()));

  if (isHashPayload) {
    batch(() => {
      dispatch(filtersUpdate(RESEARCH_STORE_NAME, filtersPayload));
      dispatch(appliedFiltersChange(filtersPayload));
    });
  } else {
    batch(() => {
      dispatch(filtersUpdate(RESEARCH_STORE_NAME, filtersState));
      dispatch(appliedFiltersChange(filtersState));
    });
  }

  dispatch(setMinMaxDatePeriod(researchFilter.dateFrom, researchFilter.dateTo));
};

export const resetFilters = (filtersUpdate) => (dispatch, getState) => {
  const initialState = getResearchFiltersInitialState();
  const { location } = getState().router;
  // Pass filters through all components
  const filtersPayload = dispatch(getFiltersPayload(initialState));
  batch(() => {
    dispatch(payloadChange(filtersPayload));
    dispatch(initFilters(filtersPayload, initialState, false, filtersUpdate));
    dispatch(appliedFiltersChange(filtersPayload));
  });
  const newSearch = removeQueryStringParam(location, ['dateFrom', 'dateTo', 'hash']);
  dispatch(push(`${ location.pathname }?${ newSearch }`));
};

export const resetResearchFilter = () => createAction(actionTypes.RESET_RESEARCH_FILTER);
